import React, { useState, useEffect } from 'react';
import { Smartphone, X } from 'lucide-react';

export default function InstallAppButton({ collapsed }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [installed, setInstalled] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(navigator.userAgent);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setInstalled(true);
      return;
    }

    // Recupera prompt catturato globalmente (prima che React si caricasse)
    if (window.__pwaPrompt) {
      setDeferredPrompt(window.__pwaPrompt);
    }

    const handler = (e) => {
      e.preventDefault();
      window.__pwaPrompt = e;
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setInstalled(true); window.__pwaPrompt = null; });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferredPrompt) {
      // Prompt nativo (Android/Chrome/Edge)
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setInstalled(true);
      setDeferredPrompt(null);
      window.__pwaPrompt = null;
    } else {
      // Nessun prompt disponibile — mostra guida
      setShowGuide(true);
    }
  };

  return (
    <>
      <button onClick={handleClick}
        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition"
        title="Installa App">
        <Smartphone size={18} className="flex-shrink-0" />
        {!collapsed && <span>Installa App</span>}
      </button>

      {showGuide && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 px-4 pb-4">
          <div className="bg-[#1e1e2f] rounded-2xl p-6 w-full max-w-sm shadow-xl border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-white">Installa ProudNoize</h3>
              <button onClick={() => setShowGuide(false)} className="p-1 hover:bg-gray-700 rounded">
                <X size={20} className="text-gray-400" />
              </button>
            </div>
            {isIOS ? (
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-start gap-3 bg-gray-800/50 p-3 rounded-lg">
                  <span className="text-lg font-bold text-yellow-400">1</span>
                  <p>Tocca <strong className="text-white">Condividi</strong> <span className="inline-block bg-gray-700 rounded px-1.5 text-xs">↑</span> in basso</p>
                </div>
                <div className="flex items-start gap-3 bg-gray-800/50 p-3 rounded-lg">
                  <span className="text-lg font-bold text-yellow-400">2</span>
                  <p>Tocca <strong className="text-white">"Aggiungi alla schermata Home"</strong></p>
                </div>
                <div className="flex items-start gap-3 bg-gray-800/50 p-3 rounded-lg">
                  <span className="text-lg font-bold text-yellow-400">3</span>
                  <p>Tocca <strong className="text-white">"Aggiungi"</strong></p>
                </div>
              </div>
            ) : isAndroid ? (
              <div className="space-y-3 text-sm text-gray-300">
                <p>Tocca il menu del browser <strong className="text-white">⋮</strong> in alto a destra, poi:</p>
                <div className="flex items-start gap-3 bg-gray-800/50 p-3 rounded-lg">
                  <span className="text-lg font-bold text-yellow-400">1</span>
                  <p>Tocca <strong className="text-white">"Installa app"</strong> o <strong className="text-white">"Aggiungi a schermata Home"</strong></p>
                </div>
                <div className="flex items-start gap-3 bg-gray-800/50 p-3 rounded-lg">
                  <span className="text-lg font-bold text-yellow-400">2</span>
                  <p>Conferma toccando <strong className="text-white">"Installa"</strong></p>
                </div>
                <p className="text-xs text-gray-500">Se non vedi l'opzione, assicurati di usare Chrome e riprova.</p>
              </div>
            ) : (
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-start gap-3 bg-gray-800/50 p-3 rounded-lg">
                  <span className="text-lg font-bold text-yellow-400">1</span>
                  <p>Usa <strong className="text-white">Chrome</strong> o <strong className="text-white">Edge</strong></p>
                </div>
                <div className="flex items-start gap-3 bg-gray-800/50 p-3 rounded-lg">
                  <span className="text-lg font-bold text-yellow-400">2</span>
                  <p>Clicca l'icona <strong className="text-white">⊕</strong> nella barra degli indirizzi</p>
                </div>
                <div className="flex items-start gap-3 bg-gray-800/50 p-3 rounded-lg">
                  <span className="text-lg font-bold text-yellow-400">3</span>
                  <p>Oppure menu <strong className="text-white">⋮ → Installa app</strong></p>
                </div>
              </div>
            )}
            <button onClick={() => setShowGuide(false)} className="btn-primary w-full mt-4">Ho capito</button>
          </div>
        </div>
      )}
    </>
  );
}
